import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { SubSink } from 'subsink';

import { VipDiscountDto } from '@app/api/marketplace/models';
import { VipDiscountsActions } from '@app/vip-discounts/store/vip-discounts.slice';
import { cancellationMockDate } from '@shared/constants/cancellation-mock-date';

import { ProductVipDiscountToMerchant } from '../../../interfaces/product-discount-to-merchant.interface';
import {
  VipDiscountsSellingActions,
  VipDiscountsSellingSelectors,
} from '../../../store/vip-discounts-selling.slice';

@Component({
  selector: 'app-vip-discounts-settings-table',
  templateUrl: './vip-discounts-settings-table.component.html',
  styleUrls: ['./vip-discounts-settings-table.component.scss'],
})
export class VipDiscountsSettingsTableComponent implements OnInit, OnDestroy {
  @Input() showCaptionRow = false;
  @Input() set tableIndex(idx: number) {
    this.isMerchantAllProductsSelected$ = this.store.select(
      VipDiscountsSellingSelectors.isMerchantAllProductsSelected(idx)
    );
  }
  @Input() discountToMerchant!: ProductVipDiscountToMerchant;

  subs = new SubSink();

  today = new Date();

  isMerchantAllProductsSelected$!: Observable<any>;
  isAllMerchantsSelected$ = this.store.select(
    VipDiscountsSellingSelectors.isAllMerchantsSelected
  );
  selectedProductsForGroupOperation$ = this.store.select(
    VipDiscountsSellingSelectors.selectSelectedProductsForGroupOperation
  );
  selectedProductsForGroupOperation: Array<string> = [];

  discountModeOptions = [
    { label: 'VIP_DISCOUNTS.PERCENT', value: 'PERCENT' },
    { label: 'VIP_DISCOUNTS.AMOUNT', value: 'AMOUNT' },
    { label: 'VIP_DISCOUNTS.FIX_PRICE', value: 'FIX_PRICE' },
  ];

  formGroup: FormGroup = new FormGroup({
    merchantId: new FormControl(''),
    vipDiscountList: new FormArray([]),
  });

  get formArray(): FormArray {
    return this.formGroup?.get('vipDiscountList') as FormArray;
  }

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.formGroup.patchValue(
      { merchantId: this.discountToMerchant.merchant.merchantId },
      { emitEvent: false }
    );
    this.formArray.clear({ emitEvent: false });
    this.discountToMerchant.vipDiscountList.forEach((product) => {
      const discountRow = new FormGroup({
        discountId: new FormControl(null), // hidden
        packagingUnitId: new FormControl(product.packagingUnitId), // hidden
        sortimentId: new FormControl(product.productSortimentId), // hidden
        discountValue: new FormControl(product.discountValue ?? null, [
          Validators.required,
          Validators.min(0.0001),
        ]),
        discountMode: new FormControl(product.discountMode ?? 'PERCENT'),
        startDate: new FormControl(product.startDate ?? new Date()),
        endDate: new FormControl(product.endDate ?? ''),
      });
      this.formArray.push(discountRow);
    });

    this.subs.sink = this.selectedProductsForGroupOperation$.subscribe(
      (selection) => {
        this.selectedProductsForGroupOperation = [];
        selection.forEach((selected) => {
          if (
            this.discountToMerchant.merchant.merchantId &&
            selected.includes(this.discountToMerchant.merchant.merchantId)
          ) {
            this.selectedProductsForGroupOperation.push(
              selected.replace(
                `${this.discountToMerchant.merchant.merchantId}#`,
                ''
              )
            );
          }
        });
      }
    );
  }

  onData(data: Array<VipDiscountDto>) {
    if (data.length) {
      data.forEach((discountDTO) => {
        const index = this.discountToMerchant.vipDiscountList.findIndex(
          (discount) => discount.packagingUnitId === discountDTO.packagingUnitId
        );
        this.formArray.at(index).patchValue({
          ...discountDTO,
          endDate:
            discountDTO.endDate === cancellationMockDate
              ? ''
              : discountDTO.endDate,
          sortimentId:
            this.discountToMerchant.vipDiscountList[index].productSortimentId,
        });
      });
    }
  }

  discountModeChanged(rowIndex: number) {
    this.formArray
      .at(rowIndex)
      .get('discountValue')
      ?.setValidators(
        this.formArray.at(rowIndex).get('discountMode')?.value === 'PERCENT'
          ? [Validators.required, Validators.min(0.0001), Validators.max(99)]
          : [Validators.required, Validators.min(0.0001)]
      );
    this.formArray.at(rowIndex).get('discountValue')?.updateValueAndValidity();
  }

  isProductChecked(packagingUnitId?: string): boolean {
    if (packagingUnitId) {
      return this.selectedProductsForGroupOperation.includes(packagingUnitId);
    }
    return false;
  }

  toggle(packagingUnitId: string) {
    this.store.dispatch(
      VipDiscountsSellingActions.selectDiscountProduct.trigger({
        merchantId: this.discountToMerchant.merchant.merchantId ?? '',
        packagingUnitId,
      })
    );
  }

  toggleAll(checked: boolean) {
    this.store.dispatch(
      VipDiscountsSellingActions.selectDiscountMerchant.trigger({
        merchantId: this.discountToMerchant.merchant.merchantId ?? '',
        packagingUnitIds: this.discountToMerchant.vipDiscountList.map(
          (discount) => discount.packagingUnitId ?? ''
        ),
        select: checked,
      })
    );
  }

  toggleAllMerchants(checked: boolean) {
    this.store.dispatch(
      VipDiscountsSellingActions.selectDiscountAllMerchants.trigger({
        select: checked,
      })
    );
  }

  updateFormByGroupDiscountOperation(value: VipDiscountDto) {
    for (let control of this.formArray.controls) {
      if (this.isProductChecked(control.get('packagingUnitId')?.value)) {
        control.patchValue({
          discountValue: value.discountValue,
          discountMode: value.discountMode,
          startDate: value.startDate,
          endDate: value.endDate,
        });
      }
    }
  }

  showOriginalPrice(index: number) {
    this.store.dispatch(
      VipDiscountsActions.loadOriginalPrice.trigger({
        discount: this.formArray.at(index).value,
        isRequestedAsDiscountReceiver: false,
      })
    );
  }

  deleteDiscount(packagingUnitId: string) {
    this.store.dispatch(
      VipDiscountsSellingActions.deleteDiscountFromStore.trigger({
        merchantId: this.discountToMerchant.merchant.merchantId ?? '',
        packagingUnitId: packagingUnitId,
      })
    );
    this.formArray.controls = this.formArray.controls.filter(
      (control) =>
        (control as FormGroup).controls['packagingUnitId'].value !==
        packagingUnitId
    );
  }

  deleteSelectedDiscounts() {
    this.selectedProductsForGroupOperation.forEach((product) => {
      const selectedProd = this.discountToMerchant.vipDiscountList.find(
        (discount) => discount.packagingUnitId === product
      );
      this.deleteDiscount(selectedProd?.packagingUnitId ?? '');
    });
  }

  markFormTouched() {
    this.formGroup.markAllAsTouched();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
